import { useState } from "react";
import { useIntl } from "@/infra/locale";
import { AppRoutes } from "@/infra/router";
import { mapLocaleToLanguage } from "@akina/domain";
import { useFetchLegalPage, useUpdateTermsAndConditionsStatus } from "@akina/infra";
import { Text, useLocaleStore } from "@akina/presentation";
import { LegalAcceptanceCheckboxRow } from "./LegalAcceptanceCheckboxRow";
import { LegalAcceptanceModalLayout } from "./LegalAcceptanceModalLayout";
import { LegalExternalLink } from "./LegalExternalLink";

type UpdatedTermsAndPrivacyPolicyAcceptanceModalProps = {
  open: boolean;
  onOpenChange: (prev: boolean) => void;
};

export function UpdatedTermsAndPrivacyPolicyAcceptanceModal({
  open,
  onOpenChange,
}: Readonly<UpdatedTermsAndPrivacyPolicyAcceptanceModalProps>) {
  const { formatMessage } = useIntl();
  const { locale } = useLocaleStore();
  const language = mapLocaleToLanguage(locale);
  const { data: termsAndConditionsLegalPage } = useFetchLegalPage(
    "terms-and-conditions",
    "care",
    language,
  );
  const { data: privacyPolicyLegalPage } = useFetchLegalPage(
    "privacy-policy-and-data-transparency",
    "care",
    language,
  );
  const [acceptPrivacyPolicy, setAcceptPrivacyPolicy] = useState(false);
  const [errorPrivacyPolicy, setErrorPrivacyPolicy] = useState(false);

  const updateStatus = useUpdateTermsAndConditionsStatus({
    onSuccess: () => onOpenChange(false),
  });

  const handleAccept = () => {
    if (acceptPrivacyPolicy && privacyPolicyLegalPage && termsAndConditionsLegalPage) {
      updateStatus.mutate({
        termsAndConditionsAccepted: true,
        dataPrivacyPolicyAccepted: true,
        termsAndConditionsAcceptedTimestamp: new Date().toISOString(),
        dataPrivacyPolicyAcceptedTimestamp: new Date().toISOString(),
        privacyPolicyVersion: privacyPolicyLegalPage.version,
        termsVersion: termsAndConditionsLegalPage.version,
      });
    } else {
      setErrorPrivacyPolicy(true);
    }
  };

  return (
    <LegalAcceptanceModalLayout
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && !acceptPrivacyPolicy) setErrorPrivacyPolicy(true);
        else onOpenChange(isOpen);
      }}
      titleMessageId="legal.updatedTermsAndPrivacyPolicyAcceptance.title"
      descriptionMessageId="legal.updatedTermsAndPrivacyPolicyAcceptance.description"
      errorMessageId="legal.updatedTermsAndPrivacyPolicyAcceptance.error"
      buttonMessageId="legal.button"
      hasError={errorPrivacyPolicy}
      isLoading={updateStatus.isPending}
      onAccept={handleAccept}>
      <LegalAcceptanceCheckboxRow
        checked={acceptPrivacyPolicy}
        onCheckedChange={setAcceptPrivacyPolicy}
        onErrorClear={() => setErrorPrivacyPolicy(false)}
        labelMessageId="legal.label">
        <LegalExternalLink route={AppRoutes.TermsAndConditions} messageId="legal.termsOfService" />
        <Text size="base" color={"text-gray-800/50"}>
          {formatMessage({ id: "legal.and" })}
        </Text>
        <LegalExternalLink
          route={AppRoutes.PrivacyPolicyAndDataTransparency}
          messageId="legal.privacyPolicy"
        />
      </LegalAcceptanceCheckboxRow>
    </LegalAcceptanceModalLayout>
  );
}
